import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { useNavigate } from 'react-router-dom';
// ============================================
// KEYFRAME ANIMATIONS
// ============================================
const fadeUp = keyframes `
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;
const badgePulse = keyframes `
  0%, 100% { box-shadow: 0 0 0 0 rgba(22, 163, 74, 0.4); }
  50% { box-shadow: 0 0 0 8px rgba(22, 163, 74, 0); }
`;
// ============================================
// STYLED COMPONENTS
// ============================================
const FullPageContainer = styled.div `
  width: 100%;
  background: var(--bg-primary);
  min-height: 100vh;
`;
// Trust Bar Section
const TrustSection = styled.section `
  background: var(--bg-secondary);
  border-top: 1px solid var(--border-color);
  border-bottom: 1px solid var(--border-color);
  padding: 48px 32px;
`;
const TrustContainer = styled.div `
  max-width: 1400px;
  margin: 0 auto;
`;
const TrustHeader = styled.div `
  text-align: center;
  margin-bottom: 32px;
`;
const TrustEyebrow = styled.p `
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.2em;
  color: var(--accent-gold);
  margin: 0 0 12px 0;
`;
const TrustTitle = styled.h2 `
  font-family: 'Playfair Display', 'Adamina', serif !important;
  font-size: 28px !important;
  font-weight: 500 !important;
  color: var(--text-primary) !important;
  margin: 0 !important;
`;
const TrustBar = styled.div `
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 56px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    gap: 32px;
  }
`;
const TrustLogo = styled.div `
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  opacity: 0;
  transform: translateY(20px);
  transition: opacity 0.5s ease-out, transform 0.5s ease-out;

  &.visible {
    opacity: 1;
    transform: translateY(0);
    transition-delay: ${props => props.delay}ms;
  }

  img {
    width: 80px;
    height: 80px;
    object-fit: contain;
    filter: grayscale(30%);
    opacity: 0.8;
    transition: all 0.25s ease;

    @media (max-width: 768px) {
      width: 60px;
      height: 60px;
    }
  }

  &:hover img {
    filter: grayscale(0%);
    opacity: 1;
    transform: scale(1.08);
  }

  span {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: var(--text-tertiary);
  }
`;
// Section Container
const Section = styled.section `
  padding: 100px 32px;
  max-width: 1400px;
  margin: 0 auto;
  opacity: 0;
  transform: translateY(50px);
  transition: opacity 700ms ease-out, transform 700ms ease-out;

  &.visible {
    opacity: 1;
    transform: translateY(0);
  }

  @media (max-width: 768px) {
    padding: 64px 20px;
  }
`;
const SectionHeader = styled.div `
  text-align: center;
  margin-bottom: 56px;
`;
const SectionEyebrow = styled.p `
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.2em;
  color: var(--accent-gold);
  margin: 0 0 12px 0;
`;
const SectionHeading = styled.h2 `
  font-family: 'Playfair Display', 'Adamina', serif !important;
  font-size: 40px !important;
  font-weight: 500 !important;
  color: var(--text-primary) !important;
  margin: 0 0 16px 0 !important;
  line-height: 1.2 !important;

  @media (max-width: 768px) {
    font-size: 32px !important;
  }
`;
const SectionSubtitle = styled.p `
  font-size: 17px;
  color: var(--text-secondary);
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.7;
`;
// Stats Grid
const StatsGrid = styled.div `
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 32px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;
const StatCard = styled.div `
  text-align: center;
  padding: 40px 24px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  opacity: 0;
  transform: translateY(30px);
  transition: all 0.3s ease;

  .visible & {
    animation: ${fadeUp} 600ms ease-out forwards;
    animation-delay: ${props => props.delay}ms;
  }

  &:hover {
    transform: translateY(-6px);
    box-shadow: var(--shadow-lg);
    border-color: var(--accent-gold);
  }
`;
const StatNumber = styled.div `
  font-family: 'Playfair Display', 'Adamina', serif;
  font-size: 56px;
  font-weight: 500;
  color: var(--accent-gold);
  line-height: 1;
  margin-bottom: 8px;

  @media (max-width: 768px) {
    font-size: 44px;
  }
`;
const StatLabel = styled.div `
  font-size: 15px;
  font-weight: 500;
  color: var(--text-primary);
  margin-bottom: 4px;
`;
const StatSublabel = styled.div `
  font-size: 13px;
  color: var(--text-tertiary);
`;
// About Section
const AboutGrid = styled.div `
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 64px;
  align-items: center;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;
const AboutImage = styled.div `
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  aspect-ratio: 4/3;
  background: var(--bg-tertiary);

  @media (max-width: 1024px) {
    order: -1;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border: 1px solid var(--border-color);
    border-radius: 8px;
    pointer-events: none;
  }
`;
const AboutContent = styled.div `
  h2 {
    font-family: 'Playfair Display', 'Adamina', serif !important;
    font-size: 36px !important;
    font-weight: 500 !important;
    color: var(--text-primary) !important;
    margin: 0 0 24px 0 !important;
    line-height: 1.2 !important;
  }

  p {
    font-size: 16px;
    color: var(--text-secondary);
    line-height: 1.8;
    margin: 0 0 16px 0;
  }

  .highlight {
    color: var(--accent-gold);
    font-weight: 600;
  }
`;
const AboutLink = styled.button `
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 24px;
  font-size: 15px;
  font-weight: 600;
  color: var(--accent-gold);
  background: transparent;
  border: none;
  cursor: pointer;
  transition: all 0.15s ease;
  padding: 0;

  &:hover {
    gap: 12px;
  }

  svg {
    transition: transform 0.15s ease;
  }

  &:hover svg {
    transform: translateX(4px);
  }
`;
// Features Grid
const FeaturesGrid = styled.div `
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 28px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;
const FeatureCard = styled.div `
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 32px;
  opacity: 0;
  transform: translateY(30px);
  transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);

  .visible & {
    animation: ${fadeUp} 600ms ease-out forwards;
    animation-delay: ${props => props.delay}ms;
  }

  &:hover {
    transform: translateY(-8px);
    border-color: var(--accent-gold);
    box-shadow: var(--shadow-lg);
  }

  h3 {
    font-family: 'Playfair Display', 'Adamina', serif !important;
    font-size: 22px !important;
    font-weight: 500 !important;
    color: var(--text-primary) !important;
    margin: 0 0 12px 0 !important;
  }

  p {
    font-size: 15px;
    color: var(--text-secondary);
    line-height: 1.7;
    margin: 0;
  }
`;
const FeatureIcon = styled.div `
  width: 52px;
  height: 52px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--accent-gold-bg);
  border-radius: 6px;
  margin-bottom: 24px;
  color: var(--accent-gold);
  transition: all 0.25s ease;

  ${FeatureCard}:hover & {
    background: var(--accent-gold);
    color: #FFFFFF;
    transform: scale(1.1);
  }
`;
const FeatureBadge = styled.span `
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 20px;
  padding: 8px 14px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-radius: 4px;
  background: var(--success-bg);
  color: var(--success-color);
  animation: ${badgePulse} 2.5s ease-in-out infinite;
`;
// CTA Section
const CTASection = styled.section `
  background: var(--bg-tertiary);
  text-align: center;
  border-top: 1px solid var(--border-color);
  border-bottom: 1px solid var(--border-color);
  padding: 100px 32px;

  @media (max-width: 768px) {
    padding: 64px 20px;
  }
`;
const CTAContent = styled.div `
  max-width: 600px;
  margin: 0 auto;
  opacity: 0;
  transform: translateY(50px);
  transition: opacity 700ms ease-out, transform 700ms ease-out;

  &.visible {
    opacity: 1;
    transform: translateY(0);
  }
`;
const CTAButtons = styled.div `
  display: flex;
  gap: 20px;
  justify-content: center;
  flex-wrap: wrap;
  margin-top: 40px;
`;
const GoldButton = styled.button `
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 16px 32px;
  font-family: 'DM Sans', 'Poppins', sans-serif;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.03em;
  border-radius: 6px;
  border: 1px solid var(--accent-gold);
  cursor: pointer;
  transition: all 0.25s ease;
  background: var(--accent-gold);
  color: #FFFFFF;

  &:hover {
    background: var(--accent-gold-dark);
    border-color: var(--accent-gold-dark);
    transform: translateY(-2px);
    box-shadow: var(--shadow-md);
  }

  @media (max-width: 480px) {
    width: 100%;
    max-width: 280px;
  }
`;
const SecondaryButton = styled.button `
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 16px 32px;
  font-family: 'DM Sans', 'Poppins', sans-serif;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.03em;
  border-radius: 6px;
  border: 1px solid var(--border-color);
  cursor: pointer;
  transition: all 0.25s ease;
  background: var(--bg-secondary);
  color: var(--text-primary);

  &:hover {
    border-color: var(--accent-gold);
    color: var(--accent-gold);
    transform: translateY(-2px);
  }

  @media (max-width: 480px) {
    width: 100%;
    max-width: 280px;
  }
`;
// Contact Section
const ContactGrid = styled.div `
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 48px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 48px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }

  @media (max-width: 768px) {
    padding: 32px 24px;
  }
`;
const ContactInfo = styled.div `
  h3 {
    font-family: 'Playfair Display', 'Adamina', serif !important;
    font-size: 28px !important;
    font-weight: 500 !important;
    color: var(--text-primary) !important;
    margin: 0 0 24px 0 !important;
  }

  p {
    font-size: 15px;
    color: var(--text-secondary);
    line-height: 1.8;
    margin: 0 0 16px 0;
  }
`;
const ContactItem = styled.div `
  display: flex;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 20px;
`;
const ContactItemIcon = styled.div `
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--accent-gold-bg);
  border-radius: 6px;
  color: var(--accent-gold);
  flex-shrink: 0;
`;
const ContactItemContent = styled.div `
  strong {
    display: block;
    font-size: 14px;
    font-weight: 600;
    color: var(--text-primary);
    margin-bottom: 4px;
  }

  span, a {
    font-size: 15px;
    color: var(--text-secondary);
  }

  a {
    color: var(--accent-gold);
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;
const ContactHours = styled.div `
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid var(--border-color);

  h4 {
    font-size: 14px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: var(--text-tertiary);
    margin: 0 0 12px 0;
  }

  p {
    font-size: 15px;
    color: var(--text-secondary);
    margin: 0;
  }
`;
// ============================================
// ICONS (inline SVG components)
// ============================================
const ArrowRightIcon = () => (_jsxs("svg", { width: "18", height: "18", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", children: [_jsx("path", { d: "M5 12h14" }), _jsx("path", { d: "m12 5 7 7-7 7" })] }));
const ShieldCheckIcon = () => (_jsxs("svg", { width: "26", height: "26", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", children: [_jsx("path", { d: "M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" }), _jsx("path", { d: "m9 12 2 2 4-4" })] }));
const AwardIcon = () => (_jsxs("svg", { width: "26", height: "26", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", children: [_jsx("circle", { cx: "12", cy: "8", r: "6" }), _jsx("path", { d: "M15.477 12.89 17 22l-5-3-5 3 1.523-9.11" })] }));
const TruckIcon = () => (_jsxs("svg", { width: "26", height: "26", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", children: [_jsx("path", { d: "M5 18H3c-.6 0-1-.4-1-1V7c0-.6.4-1 1-1h10c.6 0 1 .4 1 1v11" }), _jsx("path", { d: "M14 9h4l4 4v4c0 .6-.4 1-1 1h-2" }), _jsx("circle", { cx: "7", cy: "18", r: "2" }), _jsx("path", { d: "M15 18H9" }), _jsx("circle", { cx: "17", cy: "18", r: "2" })] }));
const CheckIcon = () => (_jsx("svg", { width: "14", height: "14", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", children: _jsx("polyline", { points: "20 6 9 17 4 12" }) }));
const MailIcon = () => (_jsxs("svg", { width: "20", height: "20", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", children: [_jsx("rect", { width: "20", height: "16", x: "2", y: "4", rx: "2" }), _jsx("path", { d: "m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" })] }));
const PhoneIcon = () => (_jsx("svg", { width: "20", height: "20", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", children: _jsx("path", { d: "M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" }) }));
const MapPinIcon = () => (_jsxs("svg", { width: "20", height: "20", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", children: [_jsx("path", { d: "M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" }), _jsx("circle", { cx: "12", cy: "10", r: "3" })] }));
const UserPlusIcon = () => (_jsxs("svg", { width: "18", height: "18", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", children: [_jsx("path", { d: "M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" }), _jsx("circle", { cx: "9", cy: "7", r: "4" }), _jsx("line", { x1: "19", x2: "19", y1: "8", y2: "14" }), _jsx("line", { x1: "22", x2: "16", y1: "11", y2: "11" })] }));
// ============================================
// MAIN COMPONENT
// ============================================
const Welcome = () => {
    const navigate = useNavigate();
    // Refs for intersection observer
    const trustRef = useRef(null);
    const statsRef = useRef(null);
    const aboutRef = useRef(null);
    const featuresRef = useRef(null);
    const ctaRef = useRef(null);
    const contactRef = useRef(null);
    const certifications = [
        { name: 'GIA', image: '/seal/gia.webp' },
        { name: 'AGS', image: '/seal/ags.webp' },
        { name: 'HRD', image: '/seal/hrd.webp' },
        { name: 'IGI', image: '/seal/igi.webp' },
        { name: 'EGL', image: '/seal/egi.webp' },
    ];
    // Intersection Observer for scroll animations
    useEffect(() => {
        const observerOptions = {
            root: null,
            rootMargin: '0px',
            threshold: 0.15
        };
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.classList.add('visible');
                }
            });
        }, observerOptions);
        // Observe all sections
        const sections = [trustRef, statsRef, aboutRef, featuresRef, ctaRef, contactRef];
        sections.forEach(ref => {
            if (ref.current) {
                observer.observe(ref.current);
            }
        });
        return () => observer.disconnect();
    }, []);
    return (_jsxs(FullPageContainer, { children: [_jsx(TrustSection, { children: _jsxs(TrustContainer, { ref: trustRef, children: [_jsxs(TrustHeader, { children: [_jsx(TrustEyebrow, { children: "Trusted Worldwide" }), _jsx(TrustTitle, { children: "Certified by the World's Finest" })] }), _jsx(TrustBar, { children: certifications.map((cert, index) => (_jsxs(TrustLogo, { delay: index * 100, className: "visible", children: [_jsx("img", { src: cert.image, alt: `${cert.name} Certification`, title: cert.name }), _jsx("span", { children: cert.name })] }, cert.name))) })] }) }), _jsxs(Section, { ref: statsRef, children: [_jsxs(SectionHeader, { children: [_jsx(SectionEyebrow, { children: "By The Numbers" }), _jsx(SectionHeading, { children: "Excellence in Every Facet" })] }), _jsxs(StatsGrid, { children: [_jsxs(StatCard, { delay: 0, children: [_jsx(StatNumber, { children: "40+" }), _jsx(StatLabel, { children: "Years of Excellence" }), _jsx(StatSublabel, { children: "Since 1984" })] }), _jsxs(StatCard, { delay: 120, children: [_jsx(StatNumber, { children: "0.40\u201320ct" }), _jsx(StatLabel, { children: "Carat Range" }), _jsx(StatSublabel, { children: "D-Z Color \u2022 IF-I1 Clarity" })] }), _jsxs(StatCard, { delay: 240, children: [_jsx(StatNumber, { children: "24/7" }), _jsx(StatLabel, { children: "Priority Shipping" }), _jsx(StatSublabel, { children: "Overnight Worldwide" })] })] })] }), _jsx(Section, { ref: aboutRef, children: _jsxs(AboutGrid, { children: [_jsx(AboutImage, { children: _jsx("img", { src: "/diamond-png-26569.png", alt: "Luxury Diamond" }) }), _jsxs(AboutContent, { children: [_jsx("h2", { children: "About Royal Gems" }), _jsxs("p", { children: ["With over ", _jsx("span", { className: "highlight", children: "40 years of global service" }), ", Royal Gems strives to present a new dimension in the diamond procurement process. We are committed to providing the highest quality natural diamonds with complete transparency and traceability from mine to market."] }), _jsxs("p", { children: ["Our large-scale inventory of natural loose diamonds includes all shapes between", ' ', _jsx("span", { className: "highlight", children: "0.40 to 20.00+ carats" }), ", D to Z and fancy color, and IF to I1 clarity. The majority of our inventory consists of ideal-excellent cut stones."] }), _jsxs(AboutLink, { onClick: () => navigate('/journey'), children: ["Learn Our Story", _jsx(ArrowRightIcon, {})] })] })] }) }), _jsxs(Section, { ref: featuresRef, children: [_jsxs(SectionHeader, { children: [_jsx(SectionEyebrow, { children: "Our Promise" }), _jsx(SectionHeading, { children: "Natural & Ethical" }), _jsx(SectionSubtitle, { children: "All diamonds supplied are purchased from legitimate sources, in compliance with United Nations Resolutions." })] }), _jsxs(FeaturesGrid, { children: [_jsxs(FeatureCard, { delay: 0, children: [_jsx(FeatureIcon, { children: _jsx(ShieldCheckIcon, {}) }), _jsx("h3", { children: "Conflict Free" }), _jsx("p", { children: "All diamonds are sourced in compliance with UN Resolutions and corresponding international laws. We guarantee conflict-free sourcing." })] }), _jsxs(FeatureCard, { delay: 120, children: [_jsx(FeatureIcon, { children: _jsx(AwardIcon, {}) }), _jsx("h3", { children: "Kimberley Process" }), _jsx("p", { children: "We follow the Kimberley Process with any rough we may use, ensuring complete certification and traceability." })] }), _jsxs(FeatureCard, { delay: 240, children: [_jsx(FeatureIcon, { children: _jsx(TruckIcon, {}) }), _jsx("h3", { children: "Priority Shipping" }), _jsx("p", { children: "Overnight shipping available worldwide for urgent requirements. We understand time-sensitive needs." }), _jsxs(FeatureBadge, { children: [_jsx(CheckIcon, {}), "Guaranteed"] })] })] })] }), _jsx(CTASection, { children: _jsxs(CTAContent, { ref: ctaRef, children: [_jsx(SectionHeading, { children: "Ready to Get Started?" }), _jsx(SectionSubtitle, { style: { marginBottom: 0 }, children: "We strongly believe that building trust and instilling confidence is the keystone to success. Subject to credit check." }), _jsxs(CTAButtons, { children: [_jsxs(GoldButton, { onClick: () => navigate('/login'), children: [_jsx(UserPlusIcon, {}), "Create Account"] }), _jsx(SecondaryButton, { onClick: () => navigate('/contact'), children: "Request Memo" })] })] }) }), _jsx(Section, { ref: contactRef, children: _jsxs(ContactGrid, { children: [_jsxs(ContactInfo, { children: [_jsx("h3", { children: "Get in Touch" }), _jsxs(ContactItem, { children: [_jsx(ContactItemIcon, { children: _jsx(MailIcon, {}) }), _jsxs(ContactItemContent, { children: [_jsx("strong", { children: "Email" }), _jsx("a", { href: "mailto:info@royalgemsusa.com", children: "info@royalgemsusa.com" })] })] }), _jsxs(ContactItem, { children: [_jsx(ContactItemIcon, { children: _jsx(PhoneIcon, {}) }), _jsxs(ContactItemContent, { children: [_jsx("strong", { children: "Phone" }), _jsx("a", { href: "tel:+15133810339", children: "1-513-381-0339" })] })] }), _jsxs(ContactItem, { children: [_jsx(ContactItemIcon, { children: _jsx(MapPinIcon, {}) }), _jsxs(ContactItemContent, { children: [_jsx("strong", { children: "Location" }), _jsx("span", { children: "Cincinnati, Ohio, USA" })] })] }), _jsxs(ContactHours, { children: [_jsx("h4", { children: "Business Hours" }), _jsx("p", { children: "Monday \u2013 Friday: 9:00 AM \u2013 6:00 PM EST" })] })] }), _jsxs(ContactInfo, { children: [_jsx("h3", { children: "Memo Terms" }), _jsx("p", { children: "We strongly believe that building trust and instilling confidence is the keystone to success. Royal Gems will help your client find the perfect diamond." }), _jsxs("p", { children: [_jsx("span", { className: "highlight", children: "Subject to credit check." }), " Contact us to learn more about our memo program and partnership opportunities."] }), _jsxs(AboutLink, { onClick: () => navigate('/contact'), style: { marginTop: 32 }, children: ["Contact Sales", _jsx(ArrowRightIcon, {})] })] })] }) })] }));
};
export default Welcome;
